// Refresh-proof generation tracker.
//
// Persists a single in-flight generation in localStorage so refresh / closing
// the tab can't lose the result. Subscribes to Supabase Realtime on the
// pending tryons row and notifies via callback when it flips to complete or
// failed. Also polls once on mount as a safety net.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";

const KEY = "fitaura.pending_generation";

export type PendingMeta = {
  id: string;
  kind: "tryon" | "outfit";
  startedAt: number;
};

export function setPendingGeneration(meta: PendingMeta) {
  try { localStorage.setItem(KEY, JSON.stringify(meta)); } catch { /* ignore */ }
}
export function clearPendingGeneration() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
export function getPendingGeneration(): PendingMeta | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingMeta;
  } catch { return null; }
}

/**
 * Wait for a tryons row to reach a terminal status. Resolves with the row
 * once `status === 'complete'` or `'failed'`, or null on timeout.
 *
 * Used by the page-level generators so that navigation/redirect happens
 * based on the row state — NOT the edge-function HTTP response. That way,
 * if the user refreshes mid-generation, the resume hook below picks it up
 * the same way.
 */
export function waitForTryonComplete(
  tryonId: string,
  timeoutMs = 3 * 60_000,
): Promise<{ status: "complete" | "failed" } | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (val: { status: "complete" | "failed" } | null) => {
      if (done) return;
      done = true;
      clearInterval(poll);
      clearTimeout(timer);
      resolve(val);
    };

    // Poll every 3s. The `tryons` table is no longer broadcast over realtime
    // (private user photos / scores), so we rely on polling exclusively.
    const poll = setInterval(async () => {
      const { data } = await supabase
        .from("tryons").select("status").eq("id", tryonId).maybeSingle();
      const status = data?.status;
      if (status === "complete" || status === "failed") finish({ status });
    }, 3000);

    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Mounted once at the app shell. On load, looks at localStorage for a
 * pending generation and:
 *  - already complete → toast + navigate to /app/result/:id
 *  - failed → toast that the credit was refunded
 *  - still processing → silently waits for the row to flip and then notifies
 */
export function useResumePendingGeneration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { refresh: refreshCredits } = useCredits();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!user || startedRef.current) return;
    startedRef.current = true;
    const pending = getPendingGeneration();
    if (!pending) return;

    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("tryons").select("status,kind")
        .eq("id", pending.id).eq("user_id", user.id).maybeSingle();

      if (cancelled) return;
      if (!data) { clearPendingGeneration(); return; }

      const handleTerminal = async (status: "complete" | "failed") => {
        clearPendingGeneration();
        await refreshCredits();
        if (status === "complete") {
          const route = pending.kind === "outfit" || data.kind === "outfit_only"
            ? `/app/result/${pending.id}`
            : `/app/result/${pending.id}`;
          toast.success("Your look is ready!", {
            action: { label: "View", onClick: () => navigate(route) },
          });
        } else {
          toast.error("Last generation failed — credit refunded.");
        }
      };

      if (data.status === "complete" || data.status === "failed") {
        await handleTerminal(data.status as "complete" | "failed");
        return;
      }

      // Still processing — wait for it.
      const result = await waitForTryonComplete(pending.id);
      if (cancelled) return;
      if (result) await handleTerminal(result.status);
      else clearPendingGeneration(); // timed out — sweeper will eventually fix the row
    })();

    return () => { cancelled = true; };
  }, [user, navigate, refreshCredits]);
}
